import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getCurrentUser, selectUserInitialized } from '../redux/slices/userSlice';
import { handleNoDataProvider } from '../redux/slices/authSlice';

/**
 * AppInitializer - Handles global app initialization logic
 * Fetches user data when user becomes authenticated
 * This component should be rendered inside the Redux Provider
 */
const AppInitializer = ({ children }) => {
    const dispatch = useDispatch();
    const isUserInitialized = useSelector(selectUserInitialized);
    const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
    const hasCalledGetCurrentUser = useRef(false);

    useEffect(() => {
        // Prevent duplicate calls (especially in React.StrictMode)
        if (hasCalledGetCurrentUser.current) {
            return;
        }

        // Only fetch user data once
        if (isUserInitialized) {
            return;
        }

        // Wait for authentication to complete
        if (!isAuthenticated) {
            return;
        }

        // Fetch user data after successful authentication
        hasCalledGetCurrentUser.current = true;
        dispatch(getCurrentUser())
            .unwrap()
            .catch((error) => {
                if (error?.status === 417) {
                    dispatch(
                        handleNoDataProvider(
                            'Logout failed while initializing app state:'
                        )
                    );
                    return;
                }

                console.error('Failed to load user data:', error);
                // Reset on error to allow retry
                hasCalledGetCurrentUser.current = false;
            });
    }, [dispatch, isUserInitialized, isAuthenticated]);

    return children;
};

export default AppInitializer;
